import type { RezaOpravneniDto } from '@gov-nx/api/generated/reza.types';
import { logger } from '@gov-nx/api/logger';
import type { Optional } from '@gov-nx/core/types/helpers.types';
import { is, PickKeys, prop } from '@gov-nx/core/types/helpers.types';
import { currentPlatform } from '@gov-nx/utils/common/Platform/platform';
import { CurrentPlatformType } from '@gov-nx/utils/common/Platform/platform.types';
import { usePersistedStore } from '../constants';
import { excludeKeys } from '../excludeKeys';
import { allowedActiveRezaKeys, allowedUserKeys } from './constants';
import { runSessionContinuityCheck } from './sessionContinuityRegistry';
import type { AuthState } from './store.types';

const _keepHistoryTrack = 3; // Only 3 last visited are shown in menu

const initialState = {
	token: undefined,
	user: undefined,
	logoutTimeStamp: undefined,
	loginFinished: false,
	// Reserved for future interactive post-switch flow; currently set only
	// during REZA transition route and not consumed elsewhere yet.
	isRezaSwitching: false,
	remainingPinAttempts: -1,
	_hasHydrated: false,
	activeReza: null,
	activeRezaDesignation: null,
	rezaHistory: [],
};

const useStore = <T>(selector: (state: AuthState) => T) =>
	usePersistedStore(
		(set, get) => ({
			...initialState,
			/** With setting access token we also set if user is successfully logged in.
			 * loginFinished is not persisted. */
			setAccessToken: (token: Optional<string>) => {
				logger('useAuthStoreBase -> setAccessToken', [token]);
				return set({
					token,
					loginFinished: !!token,
				});
			},
			setIsRezaSwitching: (isRezaSwitching: boolean) =>
				set({ isRezaSwitching }),
			logout: () =>
				set({
					...initialState,
					_hasHydrated: true,
					logoutTimeStamp: Date.now().toString(),
				}),
			authReset: () =>
				set({ ...initialState, _hasHydrated: get()._hasHydrated }),
			setUser: (user) => set({ user: PickKeys(allowedUserKeys, user) }),
			setRemainingPinAttempts: (remainingPinAttempts: number) =>
				set({ remainingPinAttempts }),
			_hasHydrated: false,
			setHasHydrated: (hasHydrated: boolean) => {
				set({
					_hasHydrated: hasHydrated,
				});
			},
			setRezaHistory: (rezaHistory: string[]) => {
				set({ rezaHistory });
			},
			setActiveReza: (
				activeReza: RezaOpravneniDto | null,
				callback?: () => void
			) => {
				const oldRezaHistory = get().rezaHistory;
				let newRezaHistory = oldRezaHistory;
				if (is(activeReza?.kod)) {
					newRezaHistory = [
						activeReza.kod,
						...oldRezaHistory.filter((kod) => kod !== activeReza.kod),
					];

					newRezaHistory = newRezaHistory.slice(0, _keepHistoryTrack);

					set({ rezaHistory: newRezaHistory });
				}
				set({
					activeReza: activeReza
						? PickKeys(allowedActiveRezaKeys, activeReza)
						: null,
					...(activeReza === null && { activeRezaDesignation: null }),
				});
				callback?.();
			},
			setActiveRezaDesignation: (designation) =>
				set({ activeRezaDesignation: designation }),
		}),
		'auth',
		selector,
		{
			onRehydrateStorage: (state) => () => {
				if (runSessionContinuityCheck()) {
					state.setAccessToken(undefined);
				}
				state.setHasHydrated(true);
			},
			/** We can not persist 'loginFinished' prop, because it is valid per instance only!!! */
			partialize: (state) =>
				excludeKeys(state, ['loginFinished', 'isRezaSwitching']),
		}
	);

export const useAuthStore = <T extends keyof AuthState>(
	property: T
): AuthState[T] => useStore(prop(property));

export const useAuthStoreUser = () => {
	const user = useAuthStore('user');
	const token = useAuthStore('token');
	const loginFinished = useAuthStore('loginFinished');

	const finished =
		currentPlatform !== CurrentPlatformType.WEB ? loginFinished : true;

	return {
		isLoggedIn: !!(token && user && finished),
		loginType: user?.typ_prihlaseni,
		loa: user?.loa,
	};
};
